import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Send, Plus, Loader2, CheckCircle2, XCircle, Pause, Play, Trash2,
  Mail, Users, Eye, MessageSquare, AlertTriangle, ArrowLeft, Zap,
  Copy, Calendar, Clock, BarChart3, RefreshCw, TestTube, X, Plug,
  List, Target, ShieldBan, Search, ChevronLeft, ChevronRight, Pencil, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtEST } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Stat Card ───

function StatCard({ icon: Icon, label, value, color = 'text-gray-900', bgColor = 'bg-gray-50', onClick }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 px-4 py-3 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300 transition-all' : ''}`}
      onClick={onClick}>
      <div className="flex items-center gap-2.5">
        <div className={`${bgColor} rounded-lg p-2`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div>
          <p className={`text-lg font-bold text-gray-900 ${onClick ? 'underline decoration-dotted underline-offset-2' : ''}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
          <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Variable Insertion Toolbar ───

function VariableToolbar({ textareaRef, onInsert }) {
  const vars = [
    { label: 'First Name', value: '{FirstName}' },
    { label: 'Last Name', value: '{LastName}' },
    { label: 'Company', value: '{Company}' },
    { label: 'Title', value: '{Title}' },
    { label: 'City', value: '{City}' },
    { label: 'State', value: '{State}' },
  ];
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      <span className="text-xs text-gray-400 mr-1 self-center">Insert:</span>
      {vars.map(v => (
        <button
          key={v.value}
          type="button"
          onClick={() => onInsert(v.value)}
          className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors font-mono"
        >
          {v.value}
        </button>
      ))}
    </div>
  );
}

// ─── Campaign Wizard ───

function CampaignWizard({ onClose, onCreated, accounts, prefill }) {
  const isEditing = prefill?.id; // If prefill has an id, we're editing an existing campaign
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Basics
  const [name, setName] = useState(prefill?.name || '');
  const [goals, setGoals] = useState(prefill?.goals || '');
  const [description, setDescription] = useState(prefill?.description || '');

  // Step 2: Audience (from saved lists)
  const [savedLists, setSavedLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [selectedListId, setSelectedListId] = useState(prefill?.list_id || '');
  const [selectedList, setSelectedList] = useState(null);
  const [listPreview, setListPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fromEmails, setFromEmails] = useState(prefill?.from_account_emails || []);

  // Step 3: Email Content — Primary (A/B optional) + Follow-up (A/B optional)
  const [abPrimary, setAbPrimary] = useState((prefill?.variants?.length || 0) > 1);
  const [primaryA, setPrimaryA] = useState(
    prefill?.variants?.[0] ? { subject: prefill.variants[0].subject, body: prefill.variants[0].body } : { subject: '', body: '' }
  );
  const [primaryB, setPrimaryB] = useState(
    prefill?.variants?.[1] ? { subject: prefill.variants[1].subject, body: prefill.variants[1].body } : { subject: '', body: '' }
  );
  const [activePrimaryTab, setActivePrimaryTab] = useState('a');

  const prefillFollowUps = prefill?.sequence_steps?.length > 0;
  const [hasFollowUp, setHasFollowUp] = useState(prefillFollowUps);
  const [followUpDelay, setFollowUpDelay] = useState(prefill?.sequence_steps?.[0]?.delay_days || 3);
  const [followUpCondition, setFollowUpCondition] = useState(prefill?.sequence_steps?.[0]?.condition || 'no_reply');
  const [abFollowUp, setAbFollowUp] = useState(false);
  const [followUpA, setFollowUpA] = useState(
    prefill?.sequence_steps?.[0] ? { subject: prefill.sequence_steps[0].subject, body: prefill.sequence_steps[0].body } : { subject: '', body: '' }
  );
  const [followUpB, setFollowUpB] = useState({ subject: '', body: '' });
  const [activeFollowUpTab, setActiveFollowUpTab] = useState('a');

  // Providers
  const defaultProviders = [
    { provider: 'instantly', percentage: 100, enabled: true, from_email: '', from_name: '' },
    { provider: 'sendgrid', percentage: 0, enabled: false, from_email: '', from_name: '' },
    { provider: 'ses', percentage: 0, enabled: false, from_email: '', from_name: '' },
  ];
  const [providerConfig, setProviderConfig] = useState(() => {
    if (prefill?.provider_config?.providers) {
      return defaultProviders.map(dp => {
        const match = prefill.provider_config.providers.find(p => p.provider === dp.provider);
        return match ? { ...dp, ...match, enabled: true } : dp;
      });
    }
    return defaultProviders;
  });
  const [providerStatuses, setProviderStatuses] = useState({});

  // Step 4: Schedule
  const [fromDate, setFromDate] = useState(prefill?.schedule?.from_date || '');
  const [toDate, setToDate] = useState(prefill?.schedule?.to_date || '');
  const [messagesPerDay, setMessagesPerDay] = useState(prefill?.schedule?.messages_per_day || 100);
  const [sendDays, setSendDays] = useState(prefill?.schedule?.send_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [sendTime, setSendTime] = useState(prefill?.schedule?.send_time || '09:00');
  const [sendTimeEnd, setSendTimeEnd] = useState(prefill?.schedule?.send_time_end || '');

  // Refs for variable insertion
  const primarySubjectRefA = useRef(null);
  const primaryBodyRefA = useRef(null);
  const primarySubjectRefB = useRef(null);
  const primaryBodyRefB = useRef(null);
  const followUpSubjectRefA = useRef(null);
  const followUpBodyRefA = useRef(null);
  const followUpSubjectRefB = useRef(null);
  const followUpBodyRefB = useRef(null);

  const insertVar = (varStr, setter, ref) => {
    setter(prev => {
      const el = ref.current;
      if (el) {
        const start = el.selectionStart || 0;
        const end = el.selectionEnd || 0;
        const field = el.tagName === 'TEXTAREA' ? 'body' : 'subject';
        const current = prev[field];
        const newVal = current.slice(0, start) + varStr + current.slice(end);
        setTimeout(() => { el.focus(); el.setSelectionRange(start + varStr.length, start + varStr.length); }, 0);
        return { ...prev, [field]: newVal };
      }
      return prev;
    });
  };

  // Load saved lists on step 2
  useEffect(() => {
    if (step === 2 && savedLists.length === 0) {
      setListsLoading(true);
      axios.get(`${API}/api/email-lists/saved-lists`, { withCredentials: true })
        .then(resp => {
          setSavedLists(resp.data);
          // Auto-select list matching prefill
          if (prefill?.list_id && resp.data.length > 0) {
            const match = resp.data.find(l => l.id === prefill.list_id);
            if (match) selectList(match.id);
          } else if (prefill?.batchName && resp.data.length > 0) {
            const match = resp.data.find(l =>
              l.filters?.batch_name === prefill.batchName ||
              l.name?.toLowerCase().includes(prefill.batchName.toLowerCase())
            );
            if (match) selectList(match.id);
          }
        })
        .catch(() => toast.error('Failed to load saved lists'))
        .finally(() => setListsLoading(false));
    }
  }, [step, savedLists.length, prefill]);

  // Fetch provider statuses on mount
  useEffect(() => {
    axios.get(`${API}/api/email-campaigns/providers/status`, { withCredentials: true }).then(r => {
      setProviderStatuses(r.data || {});
    }).catch(() => {});
  }, []);

  const selectList = async (listId) => {
    setSelectedListId(listId);
    const list = savedLists.find(l => l.id === listId);
    setSelectedList(list || null);
    if (!listId) { setListPreview(null); return; }
    setPreviewLoading(true);
    try {
      const resp = await axios.get(`${API}/api/email-lists/saved-lists/${listId}/contacts?limit=10`, { withCredentials: true });
      setListPreview(resp.data);
    } catch { toast.error('Failed to preview list'); }
    setPreviewLoading(false);
  };

  const calcDays = () => {
    if (!fromDate || !toDate) return 0;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    let count = 0;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (sendDays.includes(dayNames[d.getDay()])) count++;
    }
    return count;
  };

  const totalSendDays = calcDays();
  const totalContacts = listPreview?.total || selectedList?.contact_count || 0;
  const autoMpd = totalSendDays > 0 ? Math.ceil(totalContacts / totalSendDays) : 0;

  const saveCampaign = async (launch = false) => {
    if (!name.trim()) { toast.error('Campaign name is required'); return; }
    if (!selectedListId) { toast.error('Select a recipient list'); return; }
    if (fromEmails.length === 0) { toast.error('Select at least one From email'); return; }
    if (!primaryA.subject.trim() || !primaryA.body.trim()) { toast.error('Primary email A needs subject and body'); return; }
    if (abPrimary && (!primaryB.subject.trim() || !primaryB.body.trim())) { toast.error('Primary email B needs subject and body'); return; }
    if (hasFollowUp && (!followUpA.subject.trim() || !followUpA.body.trim())) { toast.error('Follow-up email A needs subject and body'); return; }
    if (hasFollowUp && abFollowUp && (!followUpB.subject.trim() || !followUpB.body.trim())) { toast.error('Follow-up email B needs subject and body'); return; }
    if (!fromDate || !toDate) { toast.error('Set schedule dates'); return; }

    // Build variants — primary email(s)
    const variants = [];
    if (abPrimary) {
      variants.push({ id: 'a', name: 'Variant A', subject: primaryA.subject, body: primaryA.body, weight: 50 });
      variants.push({ id: 'b', name: 'Variant B', subject: primaryB.subject, body: primaryB.body, weight: 50 });
    } else {
      variants.push({ id: 'a', name: 'Variant A', subject: primaryA.subject, body: primaryA.body, weight: 100 });
    }

    // Build sequence steps — follow-up email(s)
    const sequenceSteps = [];
    if (hasFollowUp) {
      sequenceSteps.push({
        id: 'fu_a', step_number: 2, delay_days: followUpDelay,
        condition: followUpCondition, subject: followUpA.subject, body: followUpA.body,
      });
      if (abFollowUp) {
        sequenceSteps.push({
          id: 'fu_b', step_number: 2, delay_days: followUpDelay,
          condition: followUpCondition, subject: followUpB.subject, body: followUpB.body,
          is_variant_b: true,
        });
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        goals,
        description,
        from_account_emails: fromEmails,
        list_id: selectedListId,
        variants,
        sequence_steps: sequenceSteps,
        schedule: {
          from_date: fromDate, to_date: toDate,
          messages_per_day: messagesPerDay, send_days: sendDays,
          send_time: sendTime, send_time_end: sendTimeEnd || null, timezone: 'America/New_York',
        },
        provider_config: {
          providers: providerConfig.filter(p => p.enabled && p.percentage > 0).map(p => ({
            provider: p.provider,
            percentage: p.percentage,
            from_email: p.from_email || null,
            from_name: p.from_name || null,
          })),
        },
      };
      let campaign;
      if (isEditing) {
        const resp = await axios.put(`${API}/api/email-campaigns/${prefill.id}`, payload, { withCredentials: true });
        campaign = resp.data;
      } else {
        const resp = await axios.post(`${API}/api/email-campaigns/`, payload, { withCredentials: true });
        campaign = resp.data;
      }

      if (launch) {
        try {
          const cid = campaign.id || prefill?.id;
          await axios.post(`${API}/api/email-campaigns/${cid}/launch`, {}, { withCredentials: true });
          toast.success('Campaign launched!');
        } catch (err) {
          toast.error('Campaign saved but launch failed: ' + (err.response?.data?.detail || err.message));
        }
      } else {
        toast.success(isEditing ? 'Campaign updated' : 'Campaign saved as draft');
      }
      onCreated();
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const toggleDay = (day) => {
    setSendDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  // Reusable email editor block
  const EmailEditor = ({ label, valueA, setA, valueB, setB, isAB, setIsAB, activeTab, setActiveTab, refSubA, refBodyA, refSubB, refBodyB }) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">{label}</h4>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isAB} onChange={e => setIsAB(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="text-xs text-gray-600">A/B Test</span>
        </label>
      </div>

      {isAB && (
        <div className="flex gap-1 border-b">
          {['a', 'b'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t === 'a' ? 'Version A (50%)' : 'Version B (50%)'}
            </button>
          ))}
        </div>
      )}

      {/* Version A (or only version) */}
      <div className={isAB && activeTab !== 'a' ? 'hidden' : ''}>
        <div className="mb-2">
          <Label>Subject Line *</Label>
          <VariableToolbar onInsert={(val) => {
            setA(prev => ({ ...prev, subject: prev.subject + val }));
          }} />
          <Input ref={refSubA} value={valueA.subject}
            onChange={e => setA(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="e.g. Hey {FirstName}, quick question about {Company}" className="font-mono text-sm" />
        </div>
        <div>
          <Label>Email Body *</Label>
          <VariableToolbar onInsert={(val) => {
            setA(prev => ({ ...prev, body: prev.body + val }));
          }} />
          <textarea ref={refBodyA} value={valueA.body}
            onChange={e => setA(prev => ({ ...prev, body: e.target.value }))}
            placeholder={"Hi {FirstName},\n\nI came across {Company} and wanted to reach out..."}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono min-h-[160px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      {/* Version B */}
      {isAB && (
        <div className={activeTab !== 'b' ? 'hidden' : ''}>
          <div className="mb-2">
            <Label>Subject Line * (B)</Label>
            <VariableToolbar onInsert={(val) => {
              setB(prev => ({ ...prev, subject: prev.subject + val }));
            }} />
            <Input ref={refSubB} value={valueB.subject}
              onChange={e => setB(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Alternative subject line for A/B test" className="font-mono text-sm" />
          </div>
          <div>
            <Label>Email Body * (B)</Label>
            <VariableToolbar onInsert={(val) => {
              setB(prev => ({ ...prev, body: prev.body + val }));
            }} />
            <textarea ref={refBodyB} value={valueB.body}
              onChange={e => setB(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Alternative email body for A/B test..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono min-h-[160px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{isEditing ? 'Edit Campaign' : 'New Email Campaign'}</h2>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6].map(s => (
                <div key={s} className={`w-2 h-2 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />
              ))}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Campaign Info */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Campaign Info</h3>
              <div>
                <Label>Campaign Name *</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q2 Outreach - HR Directors" />
              </div>
              <div>
                <Label>Campaign Goals</Label>
                <textarea value={goals} onChange={e => setGoals(e.target.value)}
                  placeholder="What do you want to achieve? e.g., Book 50 meetings with HR Directors at staffing agencies"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional internal notes" />
              </div>
            </div>
          )}

          {/* Step 2: Select List + From Email */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Recipients & Sender</h3>
              <p className="text-sm text-gray-500">Select a saved list of contacts and your sending account.</p>

              <div>
                <Label>Recipient List *</Label>
                {listsLoading ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading lists...</div>
                ) : savedLists.length === 0 ? (
                  <div className="border rounded-lg p-4 text-center text-sm text-gray-500">
                    <List className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No saved lists yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Go to Email List, filter your contacts, and click "Save as List".</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[220px] overflow-y-auto">
                    {savedLists.map(list => (
                      <div
                        key={list.id}
                        onClick={() => selectList(list.id)}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          selectedListId === list.id
                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{list.name}</p>
                            {list.description && <p className="text-xs text-gray-500 mt-0.5">{list.description}</p>}
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">{(list.contact_count || 0).toLocaleString()} contacts</Badge>
                        </div>
                        {list.filters && Object.keys(list.filters).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {list.filters.batch_name && <Badge variant="outline" className="text-[10px]">Batch: {list.filters.batch_name}</Badge>}
                            {list.filters.target_name && <Badge variant="outline" className="text-[10px]">Target: {list.filters.target_name}</Badge>}
                            {list.filters.status && <Badge variant="outline" className="text-[10px]">Status: {list.filters.status}</Badge>}
                            {list.filters.verify_result && <Badge variant="outline" className="text-[10px]">Verified: {list.filters.verify_result}</Badge>}
                            {list.filters.search && <Badge variant="outline" className="text-[10px]">Search: "{list.filters.search}"</Badge>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* List preview */}
              {previewLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading preview...</div>}
              {listPreview?.contacts?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs text-gray-500 font-medium">
                    Preview — {listPreview.total.toLocaleString()} total contacts (showing first 10)
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs border-t">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Company</th>
                        <th className="px-3 py-2 text-left">Title</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {listPreview.contacts.map((c, i) => (
                        <tr key={i} className="text-gray-700">
                          <td className="px-3 py-1.5">{c.first_name} {c.last_name}</td>
                          <td className="px-3 py-1.5 text-xs">{c.primary_email}</td>
                          <td className="px-3 py-1.5">{c.company}</td>
                          <td className="px-3 py-1.5 text-xs">{c.title}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <Label>From Emails * <span className="text-xs text-gray-400 font-normal ml-1">({fromEmails.length} selected — emails distributed evenly across senders)</span></Label>
                <div className="border rounded-md divide-y max-h-48 overflow-y-auto mt-1">
                  {accounts.filter(a => a.warmup_status === 'active').length > 0 && (
                    <div className="px-3 py-1.5 bg-gray-50 flex items-center justify-between">
                      <span className="text-xs text-gray-500">Warmed accounts</span>
                      <button type="button" className="text-xs text-blue-600 hover:underline"
                        onClick={() => {
                          const warmed = accounts.filter(a => a.warmup_status === 'active').map(a => a.email);
                          setFromEmails(prev => {
                            const allSelected = warmed.every(e => prev.includes(e));
                            return allSelected ? prev.filter(e => !warmed.includes(e)) : [...new Set([...prev, ...warmed])];
                          });
                        }}>
                        {accounts.filter(a => a.warmup_status === 'active').every(a => fromEmails.includes(a.email)) ? 'Deselect all warmed' : 'Select all warmed'}
                      </button>
                    </div>
                  )}
                  {accounts.map(a => (
                    <label key={a.email} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={fromEmails.includes(a.email)}
                        onChange={(e) => {
                          setFromEmails(prev => e.target.checked ? [...prev, a.email] : prev.filter(em => em !== a.email));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm flex-1 truncate">{a.email}</span>
                      {a.warmup_status === 'active' && <Badge variant="outline" className="text-[10px] text-green-600 border-green-200">warmed</Badge>}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Providers & Distribution */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700">Send Providers</h3>
              <p className="text-xs text-slate-500">Select which providers to use and set the distribution percentage.</p>

              {providerConfig.map((p, idx) => (
                <div key={p.provider} className={`p-3 rounded-lg border ${p.enabled ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={p.enabled}
                        onChange={e => {
                          const next = [...providerConfig];
                          next[idx].enabled = e.target.checked;
                          if (!e.target.checked) next[idx].percentage = 0;
                          setProviderConfig(next);
                        }}
                        className="rounded"
                      />
                      <span className="text-sm font-medium capitalize">{p.provider === 'ses' ? 'AWS SES' : p.provider === 'sendgrid' ? 'SendGrid' : 'Instantly.ai'}</span>
                      {providerStatuses[p.provider]?.ok ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Connected</span>
                      ) : (
                        <span className="text-xs text-slate-400">Not configured</span>
                      )}
                    </div>
                    {p.enabled && (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={p.percentage}
                          onChange={e => {
                            const next = [...providerConfig];
                            next[idx].percentage = parseInt(e.target.value) || 0;
                            setProviderConfig(next);
                          }}
                          className="w-16 text-center text-sm border rounded px-1 py-0.5"
                        />
                        <span className="text-xs text-slate-500">%</span>
                      </div>
                    )}
                  </div>
                  {p.enabled && p.provider !== 'instantly' && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="text-xs text-slate-500">From Email (optional override)</label>
                        <input
                          type="email"
                          value={p.from_email}
                          onChange={e => {
                            const next = [...providerConfig];
                            next[idx].from_email = e.target.value;
                            setProviderConfig(next);
                          }}
                          placeholder="Uses provider default"
                          className="w-full text-xs border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">From Name (optional override)</label>
                        <input
                          type="text"
                          value={p.from_name}
                          onChange={e => {
                            const next = [...providerConfig];
                            next[idx].from_name = e.target.value;
                            setProviderConfig(next);
                          }}
                          placeholder="Uses provider default"
                          className="w-full text-xs border rounded px-2 py-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Total percentage indicator */}
              {(() => {
                const total = providerConfig.filter(p => p.enabled).reduce((s, p) => s + p.percentage, 0);
                return (
                  <div className={`p-2 rounded-lg border text-center text-sm font-medium ${total === 100 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                    Total: {total}% {total !== 100 && '(must equal 100%)'}
                  </div>
                );
              })()}

              {/* Quick split buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setProviderConfig(prev => {
                    const enabled = prev.filter(p => p.enabled);
                    if (enabled.length === 0) return prev;
                    const each = Math.floor(100 / enabled.length);
                    const remainder = 100 - each * enabled.length;
                    return prev.map((p, i) => ({
                      ...p,
                      percentage: p.enabled ? each + (i === prev.findIndex(x => x.enabled) ? remainder : 0) : 0,
                    }));
                  })}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Equal split
                </button>
                <button
                  onClick={() => setProviderConfig(prev => prev.map(p => ({
                    ...p, enabled: p.provider === 'instantly', percentage: p.provider === 'instantly' ? 100 : 0,
                  })))}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Instantly only
                </button>
                <button
                  onClick={() => setProviderConfig(prev => prev.map(p => ({
                    ...p,
                    enabled: p.provider !== 'instantly',
                    percentage: p.provider === 'sendgrid' ? 50 : p.provider === 'ses' ? 50 : 0,
                  })))}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Direct only
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Email Content */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="font-medium text-gray-900">Email Content</h3>

              {/* Primary Email */}
              <EmailEditor
                label="Primary Email"
                valueA={primaryA} setA={setPrimaryA}
                valueB={primaryB} setB={setPrimaryB}
                isAB={abPrimary} setIsAB={setAbPrimary}
                activeTab={activePrimaryTab} setActiveTab={setActivePrimaryTab}
                refSubA={primarySubjectRefA} refBodyA={primaryBodyRefA}
                refSubB={primarySubjectRefB} refBodyB={primaryBodyRefB}
              />

              {/* Follow-up Email */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Follow-up Email</h4>
                    <p className="text-xs text-gray-400">Send a follow-up if the recipient doesn't respond</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={hasFollowUp} onChange={e => setHasFollowUp(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-xs text-gray-600">Enable Follow-up</span>
                  </label>
                </div>

                {hasFollowUp && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label>Send after (days)</Label>
                        <Input type="number" min={1} max={30} value={followUpDelay}
                          onChange={e => setFollowUpDelay(parseInt(e.target.value) || 1)} />
                      </div>
                      <div>
                        <Label>Condition</Label>
                        <select className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          value={followUpCondition} onChange={e => setFollowUpCondition(e.target.value)}>
                          <option value="no_reply">If no reply</option>
                          <option value="no_open">If not opened</option>
                          <option value="all">Send to everyone</option>
                        </select>
                      </div>
                    </div>

                    <EmailEditor
                      label="Follow-up Email"
                      valueA={followUpA} setA={setFollowUpA}
                      valueB={followUpB} setB={setFollowUpB}
                      isAB={abFollowUp} setIsAB={setAbFollowUp}
                      activeTab={activeFollowUpTab} setActiveTab={setActiveFollowUpTab}
                      refSubA={followUpSubjectRefA} refBodyA={followUpBodyRefA}
                      refSubB={followUpSubjectRefB} refBodyB={followUpBodyRefB}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Schedule */}
          {step === 5 && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Schedule</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                </div>
                <div>
                  <Label>Send Window Start (ET)</Label>
                  <Input type="time" value={sendTime} onChange={e => setSendTime(e.target.value)} />
                </div>
                <div>
                  <Label>Send Window End (ET)</Label>
                  <Input type="time" value={sendTimeEnd} onChange={e => setSendTimeEnd(e.target.value)} placeholder="e.g. 12:00" />
                </div>
              </div>
              <div>
                <Label>Messages Per Day</Label>
                <div className="flex items-center gap-3">
                  <Input type="number" min={1} max={10000} value={messagesPerDay}
                    onChange={e => setMessagesPerDay(parseInt(e.target.value) || 0)} className="w-32" />
                  {autoMpd > 0 && (
                    <button className="text-xs text-blue-600 hover:underline" onClick={() => setMessagesPerDay(autoMpd)}>
                      Auto: {autoMpd}/day to cover {totalContacts.toLocaleString()} contacts in {totalSendDays} days
                    </button>
                  )}
                </div>
              </div>
              <div>
                <Label>Send Days</Label>
                <div className="flex gap-2 mt-1">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <button key={day} onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        sendDays.includes(day) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                      {day.slice(0, 3).toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              {totalContacts > 0 && totalSendDays > 0 && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3 text-sm text-blue-800">
                      <Calendar className="w-5 h-5" />
                      <span>
                        <strong>{totalContacts.toLocaleString()}</strong> contacts over <strong>{totalSendDays}</strong> send days
                        = <strong>{messagesPerDay.toLocaleString()}</strong> per day
                        {messagesPerDay * totalSendDays < totalContacts && (
                          <span className="text-red-600 ml-2">
                            (Warning: {(totalContacts - messagesPerDay * totalSendDays).toLocaleString()} contacts won't be reached)
                          </span>
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 6: Review */}
          {step === 6 && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Review & Launch</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">Campaign</p>
                    <p className="font-medium">{name}</p>
                    {goals && <p className="text-xs text-gray-400 mt-1">{goals}</p>}
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">From Emails ({fromEmails.length} senders)</p>
                    <div className="space-y-0.5">
                      {fromEmails.map(e => <p key={e} className="text-xs font-medium">{e}</p>)}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">Recipient List</p>
                    <p className="font-medium">{selectedList?.name || '—'}</p>
                    <p className="text-xs text-gray-400">{totalContacts.toLocaleString()} contacts</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">Providers</p>
                    <div className="space-y-0.5">
                      {providerConfig.filter(p => p.enabled && p.percentage > 0).map(p => (
                        <p key={p.provider} className="text-xs font-medium">
                          {p.provider === 'ses' ? 'AWS SES' : p.provider === 'sendgrid' ? 'SendGrid' : 'Instantly.ai'} — {p.percentage}%
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">Primary Email</p>
                    <p className="text-xs text-gray-400">A: "{primaryA.subject.slice(0, 50)}..."</p>
                    {abPrimary && <p className="text-xs text-gray-400">B: "{primaryB.subject.slice(0, 50)}..."</p>}
                    {abPrimary && <Badge variant="outline" className="text-[10px] mt-1">A/B Test (50/50)</Badge>}
                  </div>
                  {hasFollowUp && (
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wide">Follow-up</p>
                      <p className="text-xs text-gray-400">After {followUpDelay}d ({followUpCondition === 'no_reply' ? 'if no reply' : followUpCondition === 'no_open' ? 'if not opened' : 'everyone'})</p>
                      <p className="text-xs text-gray-400">A: "{followUpA.subject.slice(0, 50)}..."</p>
                      {abFollowUp && <p className="text-xs text-gray-400">B: "{followUpB.subject.slice(0, 50)}..."</p>}
                      {abFollowUp && <Badge variant="outline" className="text-[10px] mt-1">A/B Test (50/50)</Badge>}
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide">Schedule</p>
                    <p className="font-medium">{fromDate} to {toDate}</p>
                    <p className="text-xs text-gray-400">{messagesPerDay}/day at {sendTime}{sendTimeEnd ? `–${sendTimeEnd}` : ''} ET on {sendDays.map(d => d.slice(0, 3)).join(', ')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t sticky bottom-0 bg-white">
          <div>
            {step > 1 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step < 6 ? (
              <Button onClick={() => {
                if (step === 3) {
                  const enabledProviders = providerConfig.filter(p => p.enabled);
                  if (enabledProviders.length === 0) { toast.error('Enable at least one provider'); return; }
                  const total = enabledProviders.reduce((s, p) => s + p.percentage, 0);
                  if (total !== 100) { toast.error('Provider percentages must add up to 100%'); return; }
                }
                setStep(step + 1);
              }}>Next</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => saveCampaign(false)} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save as Draft
                </Button>
                <Button onClick={() => saveCampaign(true)} disabled={saving} className="bg-green-600 hover:bg-green-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                  Launch Campaign
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Campaign Detail View ───

function CampaignDetail({ campaign, onBack, onRefresh, onEdit }) {
  const [actionLoading, setActionLoading] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [launchExpandedIds, setLaunchExpandedIds] = useState(new Set());
  const [contactDetail, setContactDetail] = useState(null);
  const [summaryExpandedIds, setSummaryExpandedIds] = useState(new Set());
  const [editingVariant, setEditingVariant] = useState(null); // {type:'variant'|'step', index, subject, body}
  const [editSaving, setEditSaving] = useState(false);
  const [editingSenders, setEditingSenders] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [senderEmails, setSenderEmails] = useState(campaign.from_account_emails || []);
  const [senderSaving, setSenderSaving] = useState(false);
  const [contactEmails, setContactEmails] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [sampleContact, setSampleContact] = useState(null);

  // Stats from sent/stats endpoint
  const [sentStats, setSentStats] = useState(null);

  // Pending tab state
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [nextRunAt, setNextRunAt] = useState(null);
  const [dailyLimit, setDailyLimit] = useState(campaign.schedule?.messages_per_day || 100);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState(dailyLimit);

  // Sent tab state
  const [sentItems, setSentItems] = useState([]);
  const [sentTotal, setSentTotal] = useState(0);
  const [sentSearch, setSentSearch] = useState('');
  const [sentStatusFilter, setSentStatusFilter] = useState('');
  const [sentPage, setSentPage] = useState(0);
  const [sentLoading, setSentLoading] = useState(false);

  // Override modal
  const [overrideContact, setOverrideContact] = useState(null);
  const [overrideSubject, setOverrideSubject] = useState('');
  const [overrideBody, setOverrideBody] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateVariants, setTemplateVariants] = useState(campaign.variants || []);
  const [templateSaving, setTemplateSaving] = useState(false);

  // Save as list
  const [showSaveList, setShowSaveList] = useState(false);
  const [saveListName, setSaveListName] = useState('');
  const [saveListLoading, setSaveListLoading] = useState(false);

  const pageSize = 50;

  // Fetch pending contacts
  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const params = new URLSearchParams({ skip: pendingPage * pageSize, limit: pageSize });
      if (pendingSearch) params.append('search', pendingSearch);
      const resp = await axios.get(`${API}/api/email-campaigns/${campaign.id}/pending?${params}`, { withCredentials: true });
      setPendingItems(resp.data.items || []);
      setPendingTotal(resp.data.total || 0);
      if (resp.data.next_run_at) setNextRunAt(resp.data.next_run_at);
      if (resp.data.messages_per_day) setDailyLimit(resp.data.messages_per_day);
    } catch {
      toast.error('Failed to load pending contacts');
    }
    setPendingLoading(false);
  }, [campaign.id, pendingPage, pendingSearch]);

  // Fetch sent emails
  const fetchSent = useCallback(async () => {
    setSentLoading(true);
    try {
      const params = new URLSearchParams({ skip: sentPage * pageSize, limit: pageSize });
      if (sentSearch) params.append('search', sentSearch);
      if (sentStatusFilter) params.append('status', sentStatusFilter);
      const resp = await axios.get(`${API}/api/email-campaigns/${campaign.id}/sent?${params}`, { withCredentials: true });
      setSentItems(resp.data.items || []);
      setSentTotal(resp.data.total || 0);
    } catch {
      toast.error('Failed to load sent emails');
    }
    setSentLoading(false);
  }, [campaign.id, sentPage, sentSearch, sentStatusFilter]);

  // Fetch sent stats
  const fetchSentStats = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/api/email-campaigns/${campaign.id}/sent/stats`, { withCredentials: true });
      setSentStats(resp.data);
    } catch {
      console.error('Failed to fetch sent stats');
    }
  }, [campaign.id]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  // Fetch first contact as sample data for launch preview
  useEffect(() => {
    if (showLaunchModal && !sampleContact) {
      axios.get(`${API}/api/email-campaigns/${campaign.id}/pending?skip=0&limit=1`, { withCredentials: true })
        .then(resp => {
          const items = resp.data.items || [];
          if (items.length > 0) setSampleContact(items[0]);
        })
        .catch(() => {});
    }
  }, [showLaunchModal, campaign.id, sampleContact]);
  useEffect(() => { fetchSentStats(); }, [fetchSentStats]);
  useEffect(() => { if (activeTab === 'sent') fetchSent(); }, [activeTab, fetchSent]);

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      const timeout = action === 'launch' ? 120000 : 30000; // 2 min for launch, 30s for others
      await axios.post(`${API}/api/email-campaigns/${campaign.id}/${action}`, {}, {
        withCredentials: true,
        timeout,
      });
      toast.success(
        action === 'pause' ? 'Campaign paused' :
        action === 'resume' ? 'Campaign resumed' :
        action === 'launch' ? 'Campaign launched!' :
        action === 'stop' ? 'Campaign stopped' : action
      );
      onRefresh();
    } catch (err) {
      // If launch timed out, it may still have activated — refresh to check
      if (action === 'launch' && (err.code === 'ECONNABORTED' || !err.response)) {
        toast.info('Launch is processing in the background. Refreshing...');
        onRefresh();
      } else {
        toast.error(err.response?.data?.detail || `Failed to ${action}`);
      }
    }
    setActionLoading('');
  };

  const removePending = async (contactId) => {
    try {
      await axios.delete(`${API}/api/email-campaigns/${campaign.id}/pending/${contactId}`, { withCredentials: true });
      toast.success('Contact removed from pending');
      fetchPending();
    } catch {
      toast.error('Failed to remove contact');
    }
  };

  const saveOverride = async () => {
    if (!overrideContact) return;
    setOverrideSaving(true);
    try {
      await axios.put(`${API}/api/email-campaigns/${campaign.id}/pending/${overrideContact.contact_id}/override`, {
        custom_subject: overrideSubject || null,
        custom_body: overrideBody || null,
      }, { withCredentials: true });
      toast.success('Override saved');
      setOverrideContact(null);
      fetchPending();
    } catch {
      toast.error('Failed to save override');
    }
    setOverrideSaving(false);
  };

  const clearOverride = async (contactId) => {
    try {
      await axios.delete(`${API}/api/email-campaigns/${campaign.id}/pending/${contactId}/override`, { withCredentials: true });
      toast.success('Override cleared');
      fetchPending();
    } catch {
      toast.error('Failed to clear override');
    }
  };

  const saveDailyLimit = async () => {
    try {
      await axios.put(`${API}/api/email-campaigns/${campaign.id}/daily-limit`, { messages_per_day: limitInput }, { withCredentials: true });
      toast.success('Daily limit updated');
      setDailyLimit(limitInput);
      setEditingLimit(false);
    } catch {
      toast.error('Failed to update daily limit');
    }
  };

  const saveTemplate = async () => {
    setTemplateSaving(true);
    try {
      await axios.put(`${API}/api/email-campaigns/${campaign.id}`, { variants: templateVariants }, { withCredentials: true });
      toast.success('Template updated');
      setEditingTemplate(false);
      onRefresh();
    } catch {
      toast.error('Failed to save template');
    }
    setTemplateSaving(false);
  };

  const saveAsList = async () => {
    if (!saveListName.trim()) { toast.error('Enter a list name'); return; }
    setSaveListLoading(true);
    try {
      await axios.post(`${API}/api/email-campaigns/${campaign.id}/pending/save-as-list`, { name: saveListName.trim() }, { withCredentials: true });
      toast.success('Saved as new list');
      setShowSaveList(false);
      setSaveListName('');
    } catch {
      toast.error('Failed to save as list');
    }
    setSaveListLoading(false);
  };

  const fetchAvailableAccounts = async () => {
    try {
      const resp = await axios.get(`${API}/api/email-campaigns/instantly/accounts`, { withCredentials: true });
      setAvailableAccounts(resp.data.accounts || []);
    } catch { setAvailableAccounts([]); }
  };

  const toggleSender = (email) => {
    setSenderEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const saveSenders = async () => {
    setSenderSaving(true);
    try {
      await axios.put(`${API}/api/email-campaigns/${campaign.id}`, { from_account_emails: senderEmails }, { withCredentials: true });
      toast.success(`Updated to ${senderEmails.length} senders`);
      setEditingSenders(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update senders');
    }
    setSenderSaving(false);
  };

  const saveInlineEdit = async () => {
    if (!editingVariant) return;
    setEditSaving(true);
    try {
      const payload = {};
      if (editingVariant.type === 'variant') {
        const updated = [...(campaign.variants || [])];
        updated[editingVariant.index] = { ...updated[editingVariant.index], subject: editingVariant.subject, body: editingVariant.body };
        payload.variants = updated;
      } else {
        const updated = [...(campaign.sequence_steps || [])];
        updated[editingVariant.index] = { ...updated[editingVariant.index], subject: editingVariant.subject, body: editingVariant.body };
        payload.sequence_steps = updated;
      }
      await axios.put(`${API}/api/email-campaigns/${campaign.id}`, payload, { withCredentials: true });
      toast.success('Saved');
      setEditingVariant(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setEditSaving(false);
  };

  const openContactDetail = async (contact) => {
    setContactDetail(contact);
    setContactLoading(true);
    setContactEmails([]);
    try {
      const email = contact.email || contact.primary_email;
      const resp = await axios.get(`${API}/api/email-campaigns/${campaign.id}/sent?search=${encodeURIComponent(email)}&limit=50`, { withCredentials: true });
      setContactEmails(resp.data.items || []);
    } catch {
      setContactEmails([]);
    }
    setContactLoading(false);
  };

  const refreshAll = () => {
    fetchPending();
    fetchSentStats();
    if (activeTab === 'sent') fetchSent();
    onRefresh();
  };

  // Stats values
  const stats = sentStats || {};
  const totalContacts = campaign.audience?.total_contacts || stats.total || 0;
  const totalPushed = stats.pushed || campaign.stats?.total_pushed || 0;

  const pendingTotalPages = Math.ceil(pendingTotal / pageSize);
  const sentTotalPages = Math.ceil(sentTotal / pageSize);

  const sentStatusBadge = (status) => {
    const colors = {
      pushed: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      delivered: 'bg-green-100 text-green-700',
      opened: 'bg-amber-100 text-amber-700',
      replied: 'bg-purple-100 text-purple-700',
      bounced: 'bg-red-100 text-red-700',
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  const formatDate = (d) => fmtEST(d) || '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h2 className="text-xl font-semibold">{campaign.name}</h2>
            {campaign.goals && <p className="text-sm text-gray-500">{campaign.goals}</p>}
          </div>
          <StatusBadge status={campaign.status} />
        </div>
        <div className="flex gap-2">
          {/* Edit — available for draft, paused, stopped */}
          {['draft', 'paused', 'stopped'].includes(campaign.status) && onEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(campaign)}>
              <Pencil className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}

          {/* Launch — draft only */}
          {campaign.status === 'draft' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setShowLaunchModal(true)}>
              <Zap className="w-4 h-4 mr-1" /> Launch
            </Button>
          )}

          {/* Pause — active only */}
          {campaign.status === 'active' && (
            <Button variant="outline" size="sm" onClick={() => handleAction('pause')} disabled={!!actionLoading}>
              {actionLoading === 'pause' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
              Pause
            </Button>
          )}

          {/* Resume — paused only */}
          {campaign.status === 'paused' && (
            <Button size="sm" onClick={() => handleAction('resume')} disabled={!!actionLoading}>
              {actionLoading === 'resume' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Resume
            </Button>
          )}

          {/* Stop — active or paused */}
          {['active', 'paused'].includes(campaign.status) && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => {
                if (window.confirm('Stop this campaign? This will halt all sending. You can edit and relaunch later.')) {
                  handleAction('stop');
                }
              }}
              disabled={!!actionLoading}>
              {actionLoading === 'stop' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              Stop
            </Button>
          )}

          {/* Relaunch — stopped campaigns */}
          {campaign.status === 'stopped' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setShowLaunchModal(true)}>
              <Zap className="w-4 h-4 mr-1" /> Relaunch
            </Button>
          )}

          <Button variant="ghost" size="sm" onClick={refreshAll}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Schedule Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm text-blue-800">
              <Clock className="w-5 h-5 flex-shrink-0" />
              <div>
                {nextRunAt && (
                  <p className="font-medium">
                    Next batch: {formatDate(nextRunAt)}
                    <span className="font-normal ml-1">
                      ({editingLimit ? (
                        <span className="inline-flex items-center gap-1">
                          <input type="number" value={limitInput} onChange={e => setLimitInput(parseInt(e.target.value) || 0)}
                            className="w-16 px-1 py-0.5 text-xs border rounded" min={1} max={10000} />
                          <button onClick={saveDailyLimit} className="text-blue-700 hover:text-blue-900"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => { setEditingLimit(false); setLimitInput(dailyLimit); }} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                        </span>
                      ) : (
                        <span>{dailyLimit} emails/day <button onClick={() => setEditingLimit(true)} className="text-blue-600 hover:underline text-xs ml-1">edit</button></span>
                      )})
                    </span>
                  </p>
                )}
                <p className="text-xs text-blue-600 mt-0.5">
                  {campaign.schedule?.send_days?.map(d => d.slice(0, 3).toUpperCase()).join(', ')}
                  {campaign.schedule?.send_time && <span> at {campaign.schedule.send_time}{campaign.schedule?.send_time_end ? `–${campaign.schedule.send_time_end}` : ''} ET</span>}
                  {campaign.schedule?.from_date && <span> | {campaign.schedule.from_date} to {campaign.schedule.to_date}</span>}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Summary — shown for draft, stopped, paused */}
      {['draft', 'stopped', 'paused'].includes(campaign.status) && (
        <Card>
          <CardContent className="py-4">
            <h3 className="font-medium text-gray-900 mb-3">Campaign Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">List</p>
                <p className="font-medium">{campaign.list_name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Senders</p>
                <p className="font-medium">{campaign.from_account_emails?.length || 0} accounts</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Variants</p>
                <p className="font-medium">{campaign.variants?.length || 0} {campaign.variants?.length > 1 ? '(A/B test)' : ''}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Follow-ups</p>
                <p className="font-medium">{campaign.sequence_steps?.length || 0} steps</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Providers</p>
                <p className="font-medium">
                  {campaign.provider_config?.providers?.map(p =>
                    `${p.provider === 'ses' ? 'SES' : p.provider === 'sendgrid' ? 'SendGrid' : 'Instantly'} ${p.percentage}%`
                  ).join(', ') || '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Personalization</p>
                <p className="font-medium">{campaign.use_personalization ? 'Enabled' : 'Disabled'}</p>
              </div>
            </div>
            {/* Variant previews — expandable */}
            {campaign.variants?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-gray-500 text-xs uppercase tracking-wide">Email Variants</p>
                {campaign.variants.map((v, i) => {
                  const key = `sv-${v.id || i}`;
                  const isOpen = summaryExpandedIds.has(key);
                  const isEditing = editingVariant?.type === 'variant' && editingVariant?.index === i;
                  return (
                    <div key={key} className="border rounded-lg bg-gray-50 overflow-hidden">
                      <button type="button" className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 text-left"
                        onClick={() => setSummaryExpandedIds(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; })}>
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs font-medium text-gray-700">{v.name}</span>
                          <Badge variant="outline" className="text-[10px]">{v.weight}%</Badge>
                        </div>
                        <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 border-t">
                          {isEditing ? (
                            <div className="mt-2 space-y-2">
                              <div>
                                <Label className="text-xs">Subject</Label>
                                <Input value={editingVariant.subject} onChange={e => setEditingVariant(p => ({...p, subject: e.target.value}))} className="text-sm font-mono" />
                              </div>
                              <div>
                                <Label className="text-xs">Body</Label>
                                <textarea value={editingVariant.body} onChange={e => setEditingVariant(p => ({...p, body: e.target.value}))}
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono min-h-[150px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button variant="outline" size="sm" onClick={() => setEditingVariant(null)}>Cancel</Button>
                                <Button size="sm" onClick={saveInlineEdit} disabled={editSaving}>
                                  {editSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium text-gray-900">Subject: {v.subject}</p>
                                <button className="text-xs text-blue-600 hover:underline" onClick={e => { e.stopPropagation(); setEditingVariant({ type: 'variant', index: i, subject: v.subject, body: v.body }); }}>
                                  <Pencil className="w-3 h-3 inline mr-0.5" />Edit
                                </button>
                              </div>
                              <div className="bg-white rounded border p-2 text-xs text-gray-600 whitespace-pre-line max-h-48 overflow-y-auto">{v.body}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Follow-up previews — expandable */}
            {campaign.sequence_steps?.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-gray-500 text-xs uppercase tracking-wide">Follow-up Sequence</p>
                {campaign.sequence_steps.map((s, i) => {
                  const key = `ss-${s.id || i}`;
                  const isOpen = summaryExpandedIds.has(key);
                  const isEditing = editingVariant?.type === 'step' && editingVariant?.index === i;
                  return (
                    <div key={key} className="border rounded-lg bg-amber-50 border-amber-200 overflow-hidden">
                      <button type="button" className="w-full flex items-center justify-between px-3 py-2 hover:bg-amber-100 text-left"
                        onClick={() => setSummaryExpandedIds(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; })}>
                        <div className="flex items-center gap-2">
                          <Send className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs font-medium text-amber-700">Step {s.step_number} — Day +{s.delay_days} ({s.condition})</span>
                        </div>
                        <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 border-t border-amber-200">
                          {isEditing ? (
                            <div className="mt-2 space-y-2">
                              <div>
                                <Label className="text-xs">Subject</Label>
                                <Input value={editingVariant.subject} onChange={e => setEditingVariant(p => ({...p, subject: e.target.value}))} className="text-sm font-mono" />
                              </div>
                              <div>
                                <Label className="text-xs">Body</Label>
                                <textarea value={editingVariant.body} onChange={e => setEditingVariant(p => ({...p, body: e.target.value}))}
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono min-h-[150px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button variant="outline" size="sm" onClick={() => setEditingVariant(null)}>Cancel</Button>
                                <Button size="sm" onClick={saveInlineEdit} disabled={editSaving}>
                                  {editSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-medium text-gray-900">Subject: {s.subject}</p>
                                <button className="text-xs text-blue-600 hover:underline" onClick={e => { e.stopPropagation(); setEditingVariant({ type: 'step', index: i, subject: s.subject, body: s.body }); }}>
                                  <Pencil className="w-3 h-3 inline mr-0.5" />Edit
                                </button>
                              </div>
                              <div className="bg-white rounded border p-2 text-xs text-gray-600 whitespace-pre-line max-h-48 overflow-y-auto">{s.body}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Sender Accounts */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-500 text-xs uppercase tracking-wide">Sender Accounts ({(editingSenders ? senderEmails : campaign.from_account_emails)?.length || 0})</p>
                {!editingSenders ? (
                  <button className="text-xs text-blue-600 hover:underline" onClick={() => { setEditingSenders(true); setSenderEmails(campaign.from_account_emails || []); fetchAvailableAccounts(); }}>
                    <Pencil className="w-3 h-3 inline mr-0.5" />Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => setEditingSenders(false)}>Cancel</Button>
                    <Button size="sm" className="h-6 text-xs" onClick={saveSenders} disabled={senderSaving}>
                      {senderSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save ({senderEmails.length})
                    </Button>
                  </div>
                )}
              </div>
              {editingSenders ? (
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  {availableAccounts.length === 0 ? (
                    <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400 mr-2" /> Loading accounts...</div>
                  ) : (
                    <>
                      <div className="px-3 py-1.5 bg-gray-50 flex items-center justify-between border-b">
                        <span className="text-xs text-gray-500">{availableAccounts.length} available</span>
                        <button className="text-xs text-blue-600 hover:underline"
                          onClick={() => {
                            const all = availableAccounts.map(a => a.email);
                            const allSelected = all.every(e => senderEmails.includes(e));
                            setSenderEmails(allSelected ? [] : all);
                          }}>
                          {availableAccounts.every(a => senderEmails.includes(a.email)) ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                      {availableAccounts.map(a => (
                        <label key={a.email} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer border-b last:border-0">
                          <input type="checkbox" checked={senderEmails.includes(a.email)}
                            onChange={() => toggleSender(a.email)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          <span className="text-xs font-mono flex-1 truncate">{a.email}</span>
                          {a.warmup_status === 1 && <Badge variant="outline" className="text-[10px] text-green-600 border-green-200">warmed</Badge>}
                        </label>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {(campaign.from_account_emails || []).map(email => (
                    <div key={email} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded border text-xs">
                      <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="font-mono truncate">{email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid — click any number to filter Sent tab */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Total" value={totalContacts} bgColor="bg-blue-50" color="text-blue-600"
          onClick={() => { setActiveTab('pending'); setSentStatusFilter(''); }} />
        <StatCard icon={Send} label="Pushed" value={totalPushed} bgColor="bg-indigo-50" color="text-indigo-600"
          onClick={() => { setActiveTab('sent'); setSentStatusFilter('pushed'); }} />
        <StatCard icon={Mail} label="Sent" value={stats.sent || campaign.stats?.total_sent || 0} bgColor="bg-green-50" color="text-green-600"
          onClick={() => { setActiveTab('sent'); setSentStatusFilter('sent'); }} />
        <StatCard icon={Eye} label="Opened" value={stats.opened || campaign.stats?.total_opened || 0} bgColor="bg-amber-50" color="text-amber-600"
          onClick={() => { setActiveTab('sent'); setSentStatusFilter('opened'); }} />
        <StatCard icon={MessageSquare} label="Replied" value={stats.replied || campaign.stats?.total_replied || 0} bgColor="bg-purple-50" color="text-purple-600"
          onClick={() => { setActiveTab('sent'); setSentStatusFilter('replied'); }} />
        <StatCard icon={AlertTriangle} label="Bounced" value={stats.bounced || campaign.stats?.total_bounced || 0} bgColor="bg-red-50" color="text-red-600"
          onClick={() => { setActiveTab('sent'); setSentStatusFilter('bounced'); }} />
      </div>

      {/* Tabs: Pending / Sent */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending ({pendingTotal})</TabsTrigger>
          <TabsTrigger value="sent">All ({sentTotal || stats.total || 0})</TabsTrigger>
        </TabsList>

        {/* ── Pending Tab ── */}
        <TabsContent value="pending" className="space-y-4 mt-4">
          {/* Action buttons */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowSaveList(true)}>
                <Save className="w-4 h-4 mr-1" /> Save Pending as New List
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setEditingTemplate(!editingTemplate); setTemplateVariants(campaign.variants || []); }}>
                <Pencil className="w-4 h-4 mr-1" /> {editingTemplate ? 'Cancel Edit' : 'Edit Template'}
              </Button>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search pending..." value={pendingSearch}
                onChange={e => { setPendingSearch(e.target.value); setPendingPage(0); }}
                className="pl-9" />
            </div>
          </div>

          {/* Save as List prompt */}
          {showSaveList && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <Label className="text-sm whitespace-nowrap">List Name:</Label>
                  <Input value={saveListName} onChange={e => setSaveListName(e.target.value)} placeholder="e.g. Campaign X Remaining" className="flex-1" />
                  <Button size="sm" onClick={saveAsList} disabled={saveListLoading}>
                    {saveListLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowSaveList(false)}><X className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Template Editor */}
          {editingTemplate && (
            <Card>
              <CardContent className="py-4 space-y-3">
                <h4 className="text-sm font-medium">Edit Email Template</h4>
                {templateVariants.map((v, idx) => (
                  <div key={v.id || idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{v.name || `Variant ${idx + 1}`}</span>
                      <Badge variant="outline">{v.weight}%</Badge>
                    </div>
                    <div>
                      <Label className="text-xs">Subject</Label>
                      <Input value={v.subject} onChange={e => {
                        const updated = [...templateVariants];
                        updated[idx] = { ...updated[idx], subject: e.target.value };
                        setTemplateVariants(updated);
                      }} className="font-mono text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs">Body</Label>
                      <textarea value={v.body} onChange={e => {
                        const updated = [...templateVariants];
                        updated[idx] = { ...updated[idx], body: e.target.value };
                        setTemplateVariants(updated);
                      }} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                ))}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditingTemplate(false)}>Cancel</Button>
                  <Button size="sm" onClick={saveTemplate} disabled={templateSaving}>
                    {templateSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Save Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Table */}
          <Card>
            <CardContent className="p-0">
              {pendingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : pendingItems.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  {pendingSearch ? 'No matching pending contacts' : 'No pending contacts'}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                      <th className="px-4 py-2.5">Name</th>
                      <th className="px-4 py-2.5">Email</th>
                      <th className="px-4 py-2.5">Company</th>
                      <th className="px-4 py-2.5">Title</th>
                      <th className="px-4 py-2.5 text-center">Override</th>
                      <th className="px-4 py-2.5 w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingItems.map(item => (
                      <tr key={item.contact_id} className="border-b last:border-0 hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => openContactDetail(item)}>
                        <td className="px-4 py-2.5 font-medium">{item.first_name} {item.last_name}</td>
                        <td className="px-4 py-2.5 text-xs font-mono">{item.primary_email || item.email || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{item.company || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{item.title || '—'}</td>
                        <td className="px-4 py-2.5 text-center">
                          {item.has_override ? (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px] cursor-pointer" onClick={() => clearOverride(item.contact_id)}>
                              Custom
                            </Badge>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1">
                            <button onClick={() => {
                              setOverrideContact(item);
                              setOverrideSubject(item.custom_subject || '');
                              setOverrideBody(item.custom_body || '');
                            }} className="text-gray-400 hover:text-blue-600 transition-colors p-1" title="Set override">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => removePending(item.contact_id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1" title="Remove from pending">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Pending Pagination */}
          {pendingTotalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Page {pendingPage + 1} of {pendingTotalPages} ({pendingTotal} contacts)</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={pendingPage === 0} onClick={() => setPendingPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={pendingPage >= pendingTotalPages - 1} onClick={() => setPendingPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Sent Tab ── */}
        <TabsContent value="sent" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex items-center gap-3">
            <select value={sentStatusFilter} onChange={e => { setSentStatusFilter(e.target.value); setSentPage(0); }}
              className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All Statuses</option>
              <option value="pushed">Pushed</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="replied">Replied</option>
              <option value="bounced">Bounced</option>
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search sent emails..." value={sentSearch}
                onChange={e => { setSentSearch(e.target.value); setSentPage(0); }}
                className="pl-9" />
            </div>
          </div>

          {/* Sent Table */}
          <Card>
            <CardContent className="p-0">
              {sentLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : sentItems.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  {sentSearch || sentStatusFilter ? 'No matching sent emails' : 'No sent emails yet'}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                      <th className="px-4 py-2.5">Email</th>
                      <th className="px-4 py-2.5">Name</th>
                      <th className="px-4 py-2.5">Company</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5">Pushed At</th>
                      <th className="px-4 py-2.5">Sent At</th>
                      <th className="px-4 py-2.5">Opened At</th>
                      <th className="px-4 py-2.5">Replied At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentItems.map((item, i) => (
                      <tr key={item.id || i} className="border-b last:border-0 hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => openContactDetail(item)}>
                        <td className="px-4 py-2.5 text-xs font-mono">{item.email}</td>
                        <td className="px-4 py-2.5 font-medium">{item.first_name} {item.last_name}</td>
                        <td className="px-4 py-2.5 text-gray-600">{item.company || '—'}</td>
                        <td className="px-4 py-2.5">{sentStatusBadge(item.status)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(item.pushed_at)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(item.sent_at)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(item.opened_at)}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(item.replied_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Sent Pagination */}
          {sentTotalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Page {sentPage + 1} of {sentTotalPages} ({sentTotal} emails)</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={sentPage === 0} onClick={() => setSentPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={sentPage >= sentTotalPages - 1} onClick={() => setSentPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Override Modal */}
      {overrideContact && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold">Override for {overrideContact.first_name} {overrideContact.last_name}</h3>
              <button onClick={() => setOverrideContact(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Custom Subject <span className="text-xs text-gray-400 font-normal">(leave blank to use template)</span></Label>
                <Input value={overrideSubject} onChange={e => setOverrideSubject(e.target.value)} placeholder="Custom subject line..." className="font-mono text-sm" />
              </div>
              <div>
                <Label>Custom Body <span className="text-xs text-gray-400 font-normal">(leave blank to use template)</span></Label>
                <textarea value={overrideBody} onChange={e => setOverrideBody(e.target.value)}
                  placeholder="Custom email body..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono min-h-[140px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t">
              <Button variant="outline" onClick={() => setOverrideContact(null)}>Cancel</Button>
              <Button onClick={saveOverride} disabled={overrideSaving}>
                {overrideSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save Override
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Detail Modal */}
      {contactDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-semibold text-lg">{contactDetail.first_name} {contactDetail.last_name}</h3>
                <p className="text-sm text-gray-500">{contactDetail.email || contactDetail.primary_email}</p>
              </div>
              <button onClick={() => setContactDetail(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {contactDetail.company && (
                  <div><span className="text-gray-400 text-xs uppercase">Company</span><p className="font-medium">{contactDetail.company}</p></div>
                )}
                {contactDetail.title && (
                  <div><span className="text-gray-400 text-xs uppercase">Title</span><p className="font-medium">{contactDetail.title}</p></div>
                )}
                {(contactDetail.city || contactDetail.state) && (
                  <div><span className="text-gray-400 text-xs uppercase">Location</span><p className="font-medium">{[contactDetail.city, contactDetail.state].filter(Boolean).join(', ')}</p></div>
                )}
              </div>

              {/* Next Email Preview */}
              {(() => {
                const variants = campaign.variants || [];
                const steps = campaign.sequence_steps || [];
                const sentCount = contactEmails.length;
                const c = contactDetail;
                const resolveVars = (text) => {
                  if (!text) return text;
                  return text
                    .replace(/\{FirstName\}/gi, c.first_name || '')
                    .replace(/\{LastName\}/gi, c.last_name || '')
                    .replace(/\{Company\}/gi, c.company || '')
                    .replace(/\{Title\}/gi, c.title || '')
                    .replace(/\{City\}/gi, c.city || '')
                    .replace(/\{State\}/gi, c.state || '')
                    .replace(/\{PreviousSubject\}/gi, variants[0]?.subject || '');
                };

                // Determine next email: step 1 = initial variant, step 2+ = follow-ups
                let nextEmail = null;
                let nextLabel = '';
                if (sentCount === 0 && variants.length > 0) {
                  // Pick first variant (highest weight) as preview
                  const sorted = [...variants].sort((a, b) => (b.weight || 0) - (a.weight || 0));
                  nextEmail = sorted[0];
                  nextLabel = variants.length > 1
                    ? `Initial Email (${variants.map(v => `${v.name} ${v.weight}%`).join(' / ')})`
                    : 'Initial Email';
                } else if (sentCount > 0 && sentCount <= steps.length) {
                  nextEmail = steps[sentCount - 1];
                  nextLabel = `Follow-up ${sentCount} — Day +${nextEmail.delay_days} (${nextEmail.condition})`;
                }

                if (!nextEmail || contactLoading) return null;

                // Build sending info
                const schedule = campaign.schedule || {};
                const providers = (campaign.provider_config?.providers || []).filter(p => p.percentage > 0);
                const senders = campaign.from_account_emails || [];
                const sendWindow = schedule.send_time + (schedule.send_time_end ? `–${schedule.send_time_end}` : '') + ' ET';
                const sendDaysStr = (schedule.send_days || []).map(d => d.slice(0, 3).toUpperCase()).join(', ');
                // Next send date: if nextRunAt use that, otherwise use from_date
                const nextDate = nextRunAt ? formatDate(nextRunAt) : (schedule.from_date || '—');

                return (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Next Email</p>
                    <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                      {/* Sending metadata */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Send className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs font-medium text-blue-700">{nextLabel}</span>
                        </div>
                        <span className="text-[10px] text-blue-600 font-medium"><Calendar className="w-3 h-3 inline mr-0.5" />{nextDate}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 mb-2 pb-2 border-b border-blue-200">
                        <span><strong>From:</strong> {senders.length > 0 ? senders[0] : '—'}{senders.length > 1 ? ` (+${senders.length - 1} rotating)` : ''}</span>
                        <span><strong>Via:</strong> {providers.map(p => `${p.provider === 'ses' ? 'SES' : p.provider === 'sendgrid' ? 'SendGrid' : 'Instantly'} ${p.percentage}%`).join(', ')}</span>
                        <span><strong>Window:</strong> {sendDaysStr} {sendWindow}</span>
                      </div>
                      <div className="space-y-1.5">
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase">Subject</p>
                          <p className="text-sm font-medium text-gray-900">{resolveVars(nextEmail.subject)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase">Body</p>
                          <div className="bg-white rounded border p-2 text-sm text-gray-700 whitespace-pre-line max-h-48 overflow-y-auto">
                            {resolveVars(nextEmail.body)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Email History */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Email History — {contactEmails.length} email{contactEmails.length !== 1 ? 's' : ''}
                </p>
                {contactLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : contactEmails.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No emails sent yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contactEmails.map((em, idx) => {
                      const statusColors = {
                        pushed: 'border-gray-200 bg-gray-50',
                        sent: 'border-blue-200 bg-blue-50',
                        delivered: 'border-green-200 bg-green-50',
                        opened: 'border-amber-200 bg-amber-50',
                        replied: 'border-purple-200 bg-purple-50',
                        bounced: 'border-red-200 bg-red-50',
                      };
                      return (
                        <div key={em.id || idx} className={`border rounded-lg p-3 ${statusColors[em.status] || 'border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[10px] ${
                                em.status === 'replied' ? 'bg-purple-100 text-purple-700' :
                                em.status === 'opened' ? 'bg-amber-100 text-amber-700' :
                                em.status === 'bounced' ? 'bg-red-100 text-red-700' :
                                em.status === 'sent' || em.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{em.status}</Badge>
                              <span className="text-[10px] text-gray-400">{em.provider}</span>
                              {em.variant_name && <span className="text-[10px] text-gray-400">{em.variant_name}</span>}
                            </div>
                            {em.step_number && em.step_number > 1 && (
                              <Badge variant="outline" className="text-[10px]">Follow-up {em.step_number - 1}</Badge>
                            )}
                          </div>
                          {em.subject && (
                            <p className="text-sm font-medium text-gray-900 mb-1">Subject: {em.subject}</p>
                          )}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                            {em.pushed_at && <p>Pushed: {formatDate(em.pushed_at)}</p>}
                            {em.sent_at && <p>Sent: {formatDate(em.sent_at)}</p>}
                            {em.opened_at && <p>Opened: {formatDate(em.opened_at)}</p>}
                            {em.replied_at && <p>Replied: {formatDate(em.replied_at)}</p>}
                            {em.bounced_at && <p>Bounced: {formatDate(em.bounced_at)}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Launch Confirmation Modal */}
      {showLaunchModal && (() => {
        const schedule = campaign.schedule || {};
        const totalContacts = campaign.audience?.total_contacts || pendingTotal || 0;
        const perDay = schedule.messages_per_day || 0;
        const senderCount = campaign.from_account_emails?.length || 0;
        const perAccount = senderCount > 0 ? Math.round(perDay / senderCount) : perDay;
        const providers = campaign.provider_config?.providers || [];
        const variants = campaign.variants || [];
        const steps = campaign.sequence_steps || [];
        const sendWindow = schedule.send_time + (schedule.send_time_end ? ` – ${schedule.send_time_end}` : '');
        const sendDaysList = (schedule.send_days || []).map(d => d.slice(0, 3).toUpperCase()).join(', ');

        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-lg">Launch Campaign</h3>
                </div>
                <button onClick={() => setShowLaunchModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Campaign Name */}
                <div className="text-center pb-3 border-b">
                  <h4 className="text-xl font-bold text-gray-900">{campaign.name}</h4>
                  {campaign.list_name && <p className="text-sm text-gray-500 mt-1">List: {campaign.list_name}</p>}
                </div>

                {/* Key Metrics Grid */}
                {(() => {
                  const totalPushed = campaign.stats?.total_pushed || 0;
                  const totalSent = sentStats?.total || campaign.stats?.total_sent || 0;
                  // Before launch: pending = totalContacts (nothing sent yet)
                  // After launch: pending comes from API (contact_ids - pushed)
                  const pending = pendingTotal > 0 ? pendingTotal : (totalContacts - totalPushed);
                  return (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-700">{totalContacts.toLocaleString()}</p>
                        <p className="text-xs text-blue-600 mt-0.5">Contacts in List</p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-indigo-700">{pending.toLocaleString()}</p>
                        <p className="text-xs text-indigo-600 mt-0.5">Pending to Send</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-700">{totalSent.toLocaleString()}</p>
                        <p className="text-xs text-green-600 mt-0.5">Sent</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-700">{perDay}</p>
                        <p className="text-xs text-amber-600 mt-0.5">Emails / Day</p>
                      </div>
                    </div>
                  );
                })()}
                {totalContacts > 0 && perDay > 0 && (
                  <p className="text-xs text-gray-500 text-center">
                    Estimated completion: ~{Math.ceil(totalContacts / perDay)} business days ({Math.ceil(totalContacts / perDay / 5)} weeks)
                  </p>
                )}

                {/* Schedule */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Schedule</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span><strong>Start:</strong> {schedule.from_date}</span>
                    <span className="text-gray-300">|</span>
                    <span><strong>End:</strong> {schedule.to_date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span><strong>Window:</strong> {sendWindow} ET</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <strong>Days:</strong> {sendDaysList}
                  </div>
                </div>

                {/* Sending Setup */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sending Setup</p>
                  <div className="text-sm">
                    <strong>Sender Accounts:</strong> {senderCount} ({perAccount}/day each)
                  </div>
                  <div className="text-sm">
                    <strong>Providers:</strong>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {providers.map(p => (
                        <Badge key={p.provider} className="text-xs bg-white border">
                          {p.provider === 'ses' ? 'AWS SES' : p.provider === 'sendgrid' ? 'SendGrid' : 'Instantly'} — {p.percentage}%
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Email Content — Expandable */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Email Sequence — {variants.length} variant{variants.length > 1 ? 's' : ''} + {steps.length} follow-up{steps.length !== 1 ? 's' : ''}
                  </p>
                  {sampleContact && (
                    <p className="text-[10px] text-gray-400">Preview using: {sampleContact.first_name} {sampleContact.last_name} — {sampleContact.email || sampleContact.primary_email}</p>
                  )}

                  {/* Initial Email Variants */}
                  {variants.map((v, i) => {
                    const key = `var-${v.id || i}`;
                    const isOpen = launchExpandedIds.has(key);
                    const resolveVars = (text) => {
                      if (!sampleContact || !text) return text;
                      return text
                        .replace(/\{FirstName\}/gi, sampleContact.first_name || '')
                        .replace(/\{LastName\}/gi, sampleContact.last_name || '')
                        .replace(/\{Company\}/gi, sampleContact.company || '')
                        .replace(/\{Title\}/gi, sampleContact.title || '')
                        .replace(/\{City\}/gi, sampleContact.city || '')
                        .replace(/\{State\}/gi, sampleContact.state || '');
                    };
                    return (
                      <div key={key} className="bg-white rounded-lg border overflow-hidden">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-left"
                          onClick={() => setLaunchExpandedIds(prev => {
                            const next = new Set(prev);
                            next.has(key) ? next.delete(key) : next.add(key);
                            return next;
                          })}
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-blue-500" />
                            <span className="text-sm font-medium text-gray-800">{v.name}</span>
                            <Badge variant="outline" className="text-[10px]">{v.weight}%</Badge>
                          </div>
                          <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                        </button>
                        {isOpen && (
                          <div className="px-3 pb-3 border-t bg-gray-50">
                            <div className="mt-2 space-y-1.5">
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase">Subject</p>
                                <p className="text-sm font-medium text-gray-900">{resolveVars(v.subject)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase">Body</p>
                                <div className="bg-white rounded border p-2 text-sm text-gray-700 whitespace-pre-line max-h-48 overflow-y-auto">
                                  {resolveVars(v.body)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Follow-up Steps */}
                  {steps.map((s, i) => {
                    const key = `step-${s.id || i}`;
                    const isOpen = launchExpandedIds.has(key);
                    const resolveVars = (text) => {
                      if (!sampleContact || !text) return text;
                      return text
                        .replace(/\{FirstName\}/gi, sampleContact.first_name || '')
                        .replace(/\{LastName\}/gi, sampleContact.last_name || '')
                        .replace(/\{Company\}/gi, sampleContact.company || '')
                        .replace(/\{Title\}/gi, sampleContact.title || '')
                        .replace(/\{City\}/gi, sampleContact.city || '')
                        .replace(/\{State\}/gi, sampleContact.state || '')
                        .replace(/\{PreviousSubject\}/gi, variants[0]?.subject || '');
                    };
                    return (
                      <div key={key} className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-amber-50 text-left"
                          onClick={() => setLaunchExpandedIds(prev => {
                            const next = new Set(prev);
                            next.has(key) ? next.delete(key) : next.add(key);
                            return next;
                          })}
                        >
                          <div className="flex items-center gap-2">
                            <Send className="w-3.5 h-3.5 text-amber-500" />
                            <span className="text-sm font-medium text-amber-800">Follow-up {i + 1}</span>
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Day +{s.delay_days} · {s.condition}</Badge>
                          </div>
                          <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                        </button>
                        {isOpen && (
                          <div className="px-3 pb-3 border-t border-amber-200 bg-amber-50">
                            <div className="mt-2 space-y-1.5">
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase">Subject</p>
                                <p className="text-sm font-medium text-gray-900">{resolveVars(s.subject)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase">Body</p>
                                <div className="bg-white rounded border p-2 text-sm text-gray-700 whitespace-pre-line max-h-48 overflow-y-auto">
                                  {resolveVars(s.body)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Personalization */}
                <div className="flex items-center gap-2 text-sm">
                  {campaign.use_personalization
                    ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                    : <XCircle className="w-4 h-4 text-gray-400" />}
                  <span>LLM Personalization {campaign.use_personalization ? 'enabled' : 'disabled'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowLaunchModal(false)} disabled={!!actionLoading}>
                  Cancel
                </Button>
                <Button className="bg-green-600 hover:bg-green-700" disabled={!!actionLoading}
                  onClick={async () => {
                    await handleAction('launch');
                    setShowLaunchModal(false);
                  }}>
                  {actionLoading === 'launch' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                  Confirm & Launch
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Status Badge ───

function StatusBadge({ status }) {
  const styles = {
    draft: 'bg-gray-100 text-gray-700',
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    stopped: 'bg-red-100 text-red-700',
    completed: 'bg-blue-100 text-blue-700',
    completed: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
}

// ─── Main Page ───

// ─── Suppression List Panel ───

function SuppressionListPanel({ onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const fetchSuppression = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * pageSize, limit: pageSize });
      if (search) params.append('search', search);
      if (reasonFilter) params.append('reason', reasonFilter);
      const resp = await axios.get(`${API}/api/email-campaigns/suppression-list?${params}`, { withCredentials: true });
      setItems(resp.data.items || []);
      setTotal(resp.data.total || 0);
    } catch {
      toast.error('Failed to load suppression list');
    }
    setLoading(false);
  }, [search, reasonFilter, page]);

  useEffect(() => { fetchSuppression(); }, [fetchSuppression]);

  const removeEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from suppression list? This will reactivate matching contacts.`)) return;
    try {
      await axios.delete(`${API}/api/email-campaigns/suppression-list/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success('Removed from suppression list');
      fetchSuppression();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Suppression List</h2>
            <p className="text-sm text-gray-500">{total} suppressed email{total !== 1 ? 's' : ''} (bounced / unsubscribed)</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search emails..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="pl-9"
          />
        </div>
        <select
          value={reasonFilter}
          onChange={e => { setReasonFilter(e.target.value); setPage(0); }}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All reasons</option>
          <option value="bounce">Bounce</option>
          <option value="unsubscribe">Unsubscribe</option>
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              {search || reasonFilter ? 'No matching emails found' : 'Suppression list is empty'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Reason</th>
                  <th className="px-4 py-2.5">Date Added</th>
                  <th className="px-4 py-2.5 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono text-xs">{item.email}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant={item.reason === 'bounce' ? 'destructive' : 'secondary'} className="text-xs">
                        {item.reason}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      {item.added_at ? new Date(item.added_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => removeEmail(item.email)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove from suppression list"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Page {page + 1} of {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function EmailCampaignsPage() {
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instantlyStatus, setInstantlyStatus] = useState(null);
  const [instantlyAccounts, setInstantlyAccounts] = useState([]);
  const [allProviderStatuses, setAllProviderStatuses] = useState({});
  const [showWizard, setShowWizard] = useState(false);
  const [wizardPrefill, setWizardPrefill] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showSuppression, setShowSuppression] = useState(false);
  const [deleting, setDeleting] = useState('');

  const anyProviderOk = instantlyStatus?.ok || Object.values(allProviderStatuses).some(s => s?.ok);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [campResp, statusResp, acctResp, provResp] = await Promise.all([
        axios.get(`${API}/api/email-campaigns/`, { withCredentials: true }),
        axios.get(`${API}/api/email-campaigns/instantly/status`, { withCredentials: true }),
        axios.get(`${API}/api/email-campaigns/instantly/accounts`, { withCredentials: true }),
        axios.get(`${API}/api/email-campaigns/providers/status`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setCampaigns(campResp.data);
      setInstantlyStatus(statusResp.data);
      setInstantlyAccounts(acctResp.data?.accounts || []);
      setAllProviderStatuses(provResp.data || {});
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-open wizard when navigated from EmailListPage with prefill
  useEffect(() => {
    if (location.state?.prefillBatchName) {
      setWizardPrefill({ batchName: location.state.prefillBatchName });
      setShowWizard(true);
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const refreshCampaigns = async () => {
    try {
      const resp = await axios.get(`${API}/api/email-campaigns/`, { withCredentials: true });
      setCampaigns(resp.data);
      if (selectedCampaign) {
        const updated = resp.data.find(c => c.id === selectedCampaign.id);
        if (updated) setSelectedCampaign(updated);
      }
    } catch {}
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/email-campaigns/${id}`, { withCredentials: true });
      toast.success('Campaign deleted');
      refreshCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
    setDeleting('');
  };

  // Suppression list view
  if (showSuppression) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <SuppressionListPanel onClose={() => setShowSuppression(false)} />
      </div>
    );
  }

  // Detail view
  if (selectedCampaign) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <CampaignDetail
          campaign={selectedCampaign}
          onBack={() => setSelectedCampaign(null)}
          onRefresh={refreshCampaigns}
          onEdit={(c) => {
            setWizardPrefill(c);
            setShowWizard(true);
          }}
        />
      </div>
    );
  }

  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const totalPushed = campaigns.reduce((s, c) => s + (c.stats?.total_pushed || 0), 0);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-sm text-gray-500">Create and manage email campaigns via Instantly.ai</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSuppression(true)}>
            <ShieldBan className="w-4 h-4 mr-2" /> Suppression List
          </Button>
          <Button onClick={() => setShowWizard(true)} disabled={!anyProviderOk}>
            <Plus className="w-4 h-4 mr-2" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Provider Connection Banner */}
      <Card className={anyProviderOk ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plug className={`w-5 h-5 ${anyProviderOk ? 'text-green-600' : 'text-red-600'}`} />
              <div>
                <p className="text-sm font-medium">
                  {anyProviderOk ? 'Email Providers Connected' : 'No Providers Connected'}
                </p>
                <div className="flex items-center gap-3 mt-0.5">
                  {[
                    { key: 'instantly', label: 'Instantly', status: instantlyStatus?.ok || allProviderStatuses?.instantly?.ok },
                    { key: 'sendgrid', label: 'SendGrid', status: allProviderStatuses?.sendgrid?.ok },
                    { key: 'ses', label: 'AWS SES', status: allProviderStatuses?.ses?.ok },
                  ].map(p => (
                    <span key={p.key} className={`inline-flex items-center gap-1 text-xs ${p.status ? 'text-green-600' : 'text-slate-400'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${p.status ? 'bg-green-500' : 'bg-slate-300'}`} />
                      {p.label}
                    </span>
                  ))}
                  {instantlyStatus?.ok && (
                    <span className="text-xs text-slate-400">({instantlyAccounts.length} sender{instantlyAccounts.length !== 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Mail} label="Total Campaigns" value={campaigns.length} bgColor="bg-blue-50" color="text-blue-600" />
        <StatCard icon={Zap} label="Active" value={activeCampaigns} bgColor="bg-green-50" color="text-green-600" />
        <StatCard icon={Send} label="Total Pushed" value={totalPushed} bgColor="bg-indigo-50" color="text-indigo-600" />
        <StatCard icon={Users} label="Accounts" value={instantlyAccounts.length} bgColor="bg-purple-50" color="text-purple-600" />
      </div>

      {/* Campaign List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No campaigns yet</p>
            <p className="text-sm text-gray-400 mb-4">Create your first email campaign to get started</p>
            <Button onClick={() => setShowWizard(true)} disabled={!anyProviderOk}>
              <Plus className="w-4 h-4 mr-2" /> Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map(c => {
            const total = c.audience?.total_contacts || 0;
            const pushed = c.stats?.total_pushed || 0;
            const pct = total > 0 ? Math.round((pushed / total) * 100) : 0;

            return (
              <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedCampaign(c)}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-gray-900 truncate">{c.name}</h3>
                      <p className="text-xs text-gray-400 truncate">{c.from_account_emails ? `${c.from_account_emails.length} sender${c.from_account_emails.length > 1 ? 's' : ''}` : c.from_account_email || '—'}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <StatusBadge status={c.status} />
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteCampaign(c.id); }}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        disabled={c.status === 'active'}
                      >
                        {deleting === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{pushed.toLocaleString()} / {total.toLocaleString()} pushed</span>
                      <span>{pct}%</span>
                    </div>
                    <Progress value={pct} className="h-1.5" />
                  </div>

                  {/* Schedule */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {c.schedule?.from_date} - {c.schedule?.to_date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {c.schedule?.messages_per_day}/day
                    </span>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {c.list_name && <Badge variant="outline" className="text-xs"><List className="w-3 h-3 mr-1 inline" />{c.list_name}</Badge>}
                    {c.variants?.length > 1 && <Badge variant="outline" className="text-xs">{c.variants.length} variants (A/B)</Badge>}
                    {c.provider_config?.providers?.map(p => (
                      <Badge key={p.provider} variant="outline" className="text-xs">
                        {p.provider === 'ses' ? 'SES' : p.provider === 'sendgrid' ? 'SendGrid' : 'Instantly'} {p.percentage}%
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Wizard Modal */}
      {showWizard && (
        <CampaignWizard
          accounts={instantlyAccounts}
          prefill={wizardPrefill}
          onClose={() => { setShowWizard(false); setWizardPrefill(null); }}
          onCreated={() => { setShowWizard(false); setWizardPrefill(null); refreshCampaigns(); }}
        />
      )}
    </div>
  );
}
